import {ActionBar as GitHubUiActionBar, type ActionBarProps as GitHubUiActionBarProps} from '@github-ui/action-bar'
import {testIdProps} from '@github-ui/test-id-props'
import {ActionBar as PrimerActionBar} from '@primer/react'
import {clsx} from 'clsx'
import type {ReactElement, ReactNode} from 'react'

import densityStyles from '../density-gap.module.css'
import type {StylableProps} from '../types'
import styles from './ActionBar.module.css'
import {
  type ActionBarButtonAnchorOptions,
  type ActionBarButtonAnchorRenderer,
  createActionBarButtonAnchor as createActionBarButtonAnchorFactory,
} from './ActionBarButtonAnchor'
import type {ListViewDensityToggle} from './DensityToggle'

export type ActionBarButtonAnchor = ActionBarButtonAnchorRenderer

export function createActionBarButtonAnchor(options: ActionBarButtonAnchorOptions) {
  return createActionBarButtonAnchorFactory(options)
}

// Require actionsLabel when either action type is provided and prevent mixing the two action APIs.
type ListViewActionBar =
  | {
      /**
       * Description of the controls found in the action bar, e.g., "Issue actions", "Markdown formatting tools".
       * Used as the action bar's accessible name. Required when `actions` or `actionBarItems` is provided.
       */
      actionsLabel: GitHubUiActionBarProps['label']
      /**
       * The controls to show in the action bar. These will move into a dropdown menu when the screen isn't big enough to
       * fit them side by side.
       */
      actions: GitHubUiActionBarProps['actions']
      actionBarItems?: never
    }
  | {
      /**
       * Primer ActionBar items that delegate responsive overflow behavior to Primer.
       */
      actionBarItems: ReactNode
      actionsLabel: GitHubUiActionBarProps['label']
      actions?: never
    }
  | {actionsLabel?: never; actions?: never; actionBarItems?: never}

export type ListViewActionBarProps = ListViewActionBar & {
  /** Render the density toggle beside the ActionBar, outside its overflow. */
  densityToggle?: ReactElement<typeof ListViewDensityToggle>
} & StylableProps &
  Omit<GitHubUiActionBarProps, 'actions' | 'label'>

export const ListViewActionBar = ({
  actions,
  actionBarItems,
  style,
  className,
  children,
  density,
  actionsLabel,
  densityToggle,
  overflowMenuProps,
  overflowMenuToggleProps,
  anchorRef,
}: ListViewActionBarProps & Pick<GitHubUiActionBarProps, 'overflowMenuProps' | 'overflowMenuToggleProps'>) => {
  const nonCollapsibleActions = (
    <>
      {children}
      {densityToggle}
    </>
  )

  if (actionBarItems !== undefined) {
    return (
      <div {...testIdProps('list-view-actions')} className={clsx(styles.container, className)} style={style}>
        <PrimerActionBar aria-label={actionsLabel.trim()} flush gap={density === 'none' ? 'none' : 'condensed'}>
          {actionBarItems}
        </PrimerActionBar>
        {nonCollapsibleActions}
      </div>
    )
  }

  if (actions && actions.length > 0) {
    return (
      <>
        <GitHubUiActionBar
          label={actionsLabel.trim()}
          actions={actions}
          density={density}
          overflowMenuProps={overflowMenuProps}
          overflowMenuToggleProps={overflowMenuToggleProps}
          anchorRef={anchorRef}
          {...testIdProps('list-view-actions')}
          style={style}
          className={className}
        >
          {children}
        </GitHubUiActionBar>
        {/* Keep density outside the toolbar so it never participates in overflow. */}
        {densityToggle && (
          <div {...testIdProps('legacy-density-toggle')} className={styles.legacyDensityToggle}>
            {densityToggle}
          </div>
        )}
      </>
    )
  }

  return (
    <div
      {...testIdProps('list-view-actions')}
      style={style}
      className={clsx(styles.container, density && densityStyles[density], className)}
    >
      {nonCollapsibleActions}
    </div>
  )
}
